<template>
  <main class="guests-page">
    <div class="guests-page__head">
      <h1 class="guests-page__title">Occupazione</h1>
      <div style="display: flex; gap: 10px; flex-wrap: wrap">
        <router-link to="/rooms" class="villa-btn villa-btn--ghost">
          <i class="fa-solid fa-door-open"></i> Camere
        </router-link>
        <button class="villa-btn villa-btn--ghost" @click="load">
          <i class="fa-solid fa-arrows-rotate"></i> Aggiorna
        </button>
      </div>
    </div>

    <div v-if="loading" class="guests-loading">Caricamento…</div>

    <template v-else-if="overview">
      <!-- Contatori -->
      <div class="stat-grid">
        <div class="stat-card">
          <div class="stat-card__value">{{ overview.totalRooms }}</div>
          <div class="stat-card__label">Camere</div>
        </div>
        <div class="stat-card">
          <div class="stat-card__value">{{ overview.totalBeds }}</div>
          <div class="stat-card__label">Posti letto</div>
        </div>
        <div class="stat-card">
          <div class="stat-card__value">{{ overview.occupiedBeds }}</div>
          <div class="stat-card__label">Occupati</div>
        </div>
        <div class="stat-card">
          <div class="stat-card__value">{{ overview.availableBeds }}</div>
          <div class="stat-card__label">Disponibili</div>
        </div>
        <div class="stat-card">
          <div class="stat-card__value">{{ overview.maintenanceBeds }}</div>
          <div class="stat-card__label">Manutenzione</div>
        </div>
        <div class="stat-card">
          <div class="stat-card__value">{{ overview.unavailableBeds }}</div>
          <div class="stat-card__label">Non disp.</div>
        </div>
        <div class="stat-card stat-card--accent">
          <div class="stat-card__value">{{ overview.occupancyRate }}%</div>
          <div class="stat-card__label">Occupazione</div>
        </div>
      </div>

      <div v-if="overview.rooms.length === 0" class="guests-loading">
        Nessuna camera attiva. Aggiungi camere e posti letto dalla sezione
        Camere.
      </div>

      <!-- Camere -->
      <div class="occ-grid">
        <div v-for="room in overview.rooms" :key="room.id" class="occ-room">
          <div class="occ-room__head">
            <span class="occ-room__name">{{ room.name }}</span>
            <span class="occ-room__count">
              {{ room.occupiedBeds }}/{{ room.totalBeds }} occupati
            </span>
          </div>

          <div v-if="room.beds.length === 0" class="beds-empty">
            Nessun posto letto.
          </div>

          <div v-for="bed in room.beds" :key="bed.id" class="bed-row">
            <div class="bed-row__left">
              <span class="bed-row__code">{{ bed.code }}</span>
              <span :class="['bed-badge', 'bed-badge--' + bed.status]">
                {{ bedStatusLabel(bed.status) }}
              </span>
              <span v-if="bed.guest" class="bed-row__occupant">
                {{ bed.guest.firstName }} {{ bed.guest.lastName }}
              </span>
            </div>
            <div class="bed-row__actions">
              <button
                v-if="bed.status === 'available'"
                class="villa-btn villa-btn--primary"
                @click="openAssign(room, bed)"
              >
                <i class="fa-solid fa-user-plus"></i> Assegna
              </button>
              <button
                v-else-if="bed.status === 'occupied'"
                class="villa-btn villa-btn--danger"
                @click="openRelease(room, bed)"
              >
                <i class="fa-solid fa-right-from-bracket"></i> Libera
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Modale assegnazione -->
    <VueFinalModal v-model="assignModal.open" content-class="villa-modal">
      <h3 class="villa-modal__title">Assegna ospite</h3>
      <p class="villa-confirm__text">
        Camera <strong>{{ assignModal.roomName }}</strong> · posto letto
        <strong>{{ assignModal.bedCode }}</strong>
      </p>
      <div class="form-grid">
        <div class="form-field form-field--full">
          <label>Ospite (attivo) <span class="required">*</span></label>
          <select v-model="assignModal.guestId">
            <option :value="null" disabled>— seleziona ospite —</option>
            <option v-for="g in assignModal.guests" :key="g.id" :value="g.id">
              {{ g.lastName }} {{ g.firstName }}
            </option>
          </select>
          <span
            v-if="assignModal.guests.length === 0"
            class="bed-row__occupant"
          >
            Nessun ospite attivo disponibile. Creane uno dalla sezione Ospiti.
          </span>
        </div>
        <div class="form-field">
          <label>Data inizio</label>
          <input v-model="assignModal.startDate" type="date" />
        </div>
        <div class="form-field form-field--full">
          <label>Note</label>
          <textarea v-model.trim="assignModal.notes" rows="2"></textarea>
        </div>
      </div>
      <div class="form-actions">
        <button
          class="villa-btn villa-btn--ghost"
          @click="assignModal.open = false"
        >
          Annulla
        </button>
        <button
          class="villa-btn villa-btn--primary"
          :disabled="assignModal.busy || !assignModal.guestId"
          @click="submitAssign"
        >
          {{ assignModal.busy ? "Assegna…" : "Assegna" }}
        </button>
      </div>
    </VueFinalModal>

    <!-- Conferma rilascio -->
    <VueFinalModal v-model="releaseConfirm.open" content-class="villa-confirm">
      <h3 class="villa-confirm__title">Libera posto letto</h3>
      <p class="villa-confirm__text">
        Liberare il posto letto <strong>{{ releaseConfirm.bedCode }}</strong>
        <template v-if="releaseConfirm.guestName">
          (occupato da {{ releaseConfirm.guestName }})</template
        >?
      </p>
      <div class="villa-confirm__actions">
        <button
          class="villa-btn villa-btn--ghost"
          :disabled="releaseConfirm.busy"
          @click="releaseConfirm.open = false"
        >
          Annulla
        </button>
        <button
          class="villa-btn villa-btn--danger"
          :disabled="releaseConfirm.busy"
          @click="submitRelease"
        >
          {{ releaseConfirm.busy ? "Libera…" : "Libera" }}
        </button>
      </div>
    </VueFinalModal>
  </main>
</template>

<script>
import { mapActions } from "vuex";

const todayISO = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
};

export default {
  name: "OccupancyPage",
  data() {
    return {
      overview: null,
      loading: true,
      assignModal: {
        open: false,
        busy: false,
        bedId: null,
        bedCode: "",
        roomName: "",
        guestId: null,
        startDate: todayISO(),
        notes: "",
        guests: [],
      },
      releaseConfirm: {
        open: false,
        busy: false,
        bedId: null,
        bedCode: "",
        guestName: "",
      },
    };
  },
  methods: {
    ...mapActions([
      "fetchOccupancyOverview",
      "assignGuestToBed",
      "releaseAssignment",
      "fetchGuests",
    ]),

    bedStatusLabel(s) {
      return (
        {
          available: "Disponibile",
          occupied: "Occupato",
          maintenance: "Manutenzione",
          unavailable: "Non disponibile",
        }[s] || s
      );
    },

    async load() {
      this.loading = true;
      try {
        this.overview = await this.fetchOccupancyOverview();
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.error ||
            "Errore nel caricamento dell'occupazione."
        );
      } finally {
        this.loading = false;
      }
    },

    async openAssign(room, bed) {
      this.assignModal = {
        open: true,
        busy: false,
        bedId: bed.id,
        bedCode: bed.code,
        roomName: room.name,
        guestId: null,
        startDate: todayISO(),
        notes: "",
        guests: [],
      };
      try {
        const all = await this.fetchGuests({ page: 1, limit: 1000 });
        this.assignModal.guests = (Array.isArray(all) ? all : []).filter(
          (g) => g.status === "active"
        );
      } catch (err) {
        if (err?.response?.status !== 404) {
          this.$toast.error("Impossibile caricare la lista ospiti.");
        }
        this.assignModal.guests = [];
      }
    },
    async submitAssign() {
      if (!this.assignModal.guestId) return;
      this.assignModal.busy = true;
      try {
        await this.assignGuestToBed({
          guestId: this.assignModal.guestId,
          bedId: this.assignModal.bedId,
          startDate: this.assignModal.startDate || undefined,
          notes: this.assignModal.notes || undefined,
        });
        this.$toast.success("Ospite assegnato.");
        this.assignModal.open = false;
        await this.load();
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.error ||
            err?.response?.data?.details ||
            "Assegnazione non riuscita."
        );
      } finally {
        this.assignModal.busy = false;
      }
    },

    openRelease(room, bed) {
      this.releaseConfirm = {
        open: true,
        busy: false,
        bedId: bed.id,
        bedCode: bed.code,
        guestName: bed.guest
          ? `${bed.guest.firstName} ${bed.guest.lastName}`
          : "",
      };
    },
    async submitRelease() {
      this.releaseConfirm.busy = true;
      try {
        await this.releaseAssignment({ bedId: this.releaseConfirm.bedId });
        this.$toast.success("Posto letto liberato.");
        this.releaseConfirm.open = false;
        await this.load();
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.error || "Rilascio non riuscito."
        );
      } finally {
        this.releaseConfirm.busy = false;
      }
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style src="@/css/GuestsPage.css" />
<style src="@/css/RoomsPage.css" />
